import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Heart, MessageCircle, TrendingUp, 
  Edit3, Brain, AlertTriangle, FileText, 
  Shield, Plus, ChevronRight, Moon, Sun, Lock, Zap, CheckCircle, Mic, Video, Headphones, Activity,
  CreditCard, Calendar, DollarSign
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useJournal } from '../contexts/JournalContext';
import { useAIAnalysis } from '../contexts/AIAnalysisContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useCrisis } from '../contexts/CrisisContext'; 
import { useFeatureGate } from '../contexts/FeatureGateContext';
import { format } from 'date-fns';
import AvatarSessionModal from './AvatarSessionModal';
import { SessionType } from '../lib/tavus';
import UpcomingFeaturesCard from './UpcomingFeaturesCard';
import Navigation from './Navigation';

interface DashboardProps {
  setAvatarCompanionState: React.Dispatch<React.SetStateAction<{
    isVisible: boolean;
    sessionType: SessionType;
  }>>;
}

const Dashboard: React.FC<DashboardProps> = ({ setAvatarCompanionState }) => {
  const { user, logout, isLoading } = useAuth();
  const location = useLocation();
  const hasRedirectedRef = useRef(false);
  const { stats, entries } = useJournal();
  const { getInsights } = useAIAnalysis();
  const { subscription, checkUsageLimit, trackFeatureUsage, getRemainingUsage } = useSubscription();
  const { currentCrisisLevel, safetyPlan } = useCrisis();
  const { isFeatureEnabled, getWeekNumber, checkAndTriggerUnlockNotifications } = useFeatureGate();
  const navigate = useNavigate();
  
  const [currentMood, setCurrentMood] = useState(5);
  const [showMoodSelector, setShowMoodSelector] = useState(false);
  const [timeOfDay, setTimeOfDay] = useState<'morning' | 'afternoon' | 'evening'>('morning');
  const [showFeatureTour, setShowFeatureTour] = useState(false);
  const [showAvatarSessionModal, setShowAvatarSessionModal] = useState<boolean>(false);
  const [selectedSessionType, setSelectedSessionType] = useState<SessionType>('check-in');
  const [featureLockedModal, setFeatureLockedModal] = useState<string | null>(null);

  // Debug state changes
  useEffect(() => {
    console.log('🎭 showAvatarSessionModal state changed to:', showAvatarSessionModal);
  }, [showAvatarSessionModal]);

  useEffect(() => {
    // Reset redirect flag when location changes
    hasRedirectedRef.current = false;
  }, [location.pathname]);

  useEffect(() => {
    // Don't perform any navigation logic if auth is still loading
    if (isLoading) {
      console.log("Auth is still loading in Dashboard, waiting for user data...");
      return;
    }

    if (!user) {
      console.log("No user found in Dashboard, redirecting to auth. Auth loading state:", isLoading);
      if (!hasRedirectedRef.current) {
        hasRedirectedRef.current = true;
        navigate('/auth');
      }
      return;
    }

    // Mark that user has visited dashboard in this session
    const sessionKey = `dashboard_visited_${user.id}`;
    const hasVisitedDashboard = sessionStorage.getItem(sessionKey) === 'true';

    // Set time of day for greeting
    const hour = new Date().getHours();
    if (hour < 12) setTimeOfDay('morning');
    else if (hour < 18) setTimeOfDay('afternoon');
    else setTimeOfDay('evening');

    // Redirect to onboarding if not completed AND hasn't visited dashboard yet in this session
    if (!user.hasCompletedOnboarding && !hasVisitedDashboard) {
      console.log("User has not completed onboarding and hasn't visited dashboard yet, redirecting to onboarding flow. User:", user.id, "isAdmin:", user.isAdmin, "isDemoAccount:", user.isDemoAccount);
      if (!hasRedirectedRef.current) {
        hasRedirectedRef.current = true;
        navigate('/onboarding');
      }
      return;
    }

    // Mark dashboard as visited for this session
    if (!hasVisitedDashboard) {
      sessionStorage.setItem(sessionKey, 'true');
      console.log("Marked dashboard as visited for session. User:", user.id);
    }

    console.log("Showing dashboard. User:", user.id, "isAdmin:", user.isAdmin, "hasCompletedOnboarding:", user.hasCompletedOnboarding, "hasVisitedDashboard:", hasVisitedDashboard);
  }, [user, navigate, isLoading, location.pathname]);

  // Check for unlock notifications only once per day, when user is engaged
  useEffect(() => {
    if (!user) return;

    const lastNotificationCheck = localStorage.getItem(`last_unlock_check_${user.id}`);
    const today = new Date().toDateString();

    // Only check once per day
    if (lastNotificationCheck !== today) {
      // Wait 3 seconds after dashboard loads to check for new features
      // This ensures user is actually engaged with the app
      const timer = setTimeout(() => {
        checkAndTriggerUnlockNotifications();
        localStorage.setItem(`last_unlock_check_${user.id}`, today);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [user, checkAndTriggerUnlockNotifications]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleJournalClick = () => {
    console.log("Attempting to navigate to /journal/new from Dashboard.");
    if (checkUsageLimit('journal_entry')) {
      trackFeatureUsage('journal_entry');
      navigate('/journal/new');
    } else {
      console.log("Journal entry limit reached. Redirecting to subscription plans.");
      navigate('/subscription/plans');
    }
  };

  const handleFeatureClick = (feature: string, path: string, isAvatar = false) => {
    console.log(`handleFeatureClick called: feature=${feature}, path=${path}, isAvatar=${isAvatar}`);

    // Check if feature is enabled based on user tenure
    if (!isFeatureEnabled(feature)) {
      console.log(`Feature ${feature} is not enabled, showing locked modal`);
      setFeatureLockedModal(feature);
      return;
    }

    console.log(`Feature ${feature} is enabled, checking usage limit`);

    // Check subscription limits
    if (checkUsageLimit(feature)) {
      console.log(`Usage limit OK for ${feature}, tracking usage`);
      trackFeatureUsage(feature);

      // Special handling for avatar companion
      if (isAvatar) {
        // Show session selection modal instead of navigating
        console.log(`Avatar feature clicked, showing session modal`);
        setShowAvatarSessionModal(true);
      } else {
        // Normal navigation for other features
        console.log(`Non-avatar feature, navigating to ${path}`);
        navigate(path);
      }
    } else {
      // Show upgrade prompt
      console.log(`Usage limit reached for ${feature}, redirecting to subscription plans`);
      navigate('/subscription/plans');
    }
  };

  const getFeatureWeek = (feature: string): number => {
    if (feature === 'journal_entry' || feature === 'voice_journal' || 
        feature === 'basic_insights' || feature === 'crisis_support' || 
        feature === 'simple_progress' || feature === 'safety_plan') {
      return 1;
    } else if (feature === 'avatar_companion' || feature === 'audio_library' || 
               feature === 'goal_adjustment' || feature === 'basic_behavioral') {
      return 2;
    } else if (feature === 'advanced_analytics' || feature === 'detailed_behavioral' || 
               feature === 'custom_audio' || feature === 'therapist_matching') {
      return 3;
    } else {
      return 4;
    }
  };

  // Get feature details for the feature-locked modal
  const getFeatureDetails = (featureId: string) => {
    // Map feature IDs to user-friendly names and descriptions
    const featureDetails: Record<string, { name: string; description: string; icon: React.ElementType; week: number }> = {
      'journal_entry': { 
        name: 'Journal Entries', 
        description: 'Express your thoughts and track your mood with text journaling',
        icon: BookOpen,
        week: 1
      },
      'voice_journal': { 
        name: 'Voice Journaling', 
        description: 'Record your thoughts naturally with voice-to-text transcription',
        icon: Mic,
        week: 1
      },
      'avatar_companion': { 
        name: 'AI Avatar Companion', 
        description: 'Have face-to-face conversations with your personalized AI therapist',
        icon: Video,
        week: 2
      },
      'audio_library': { 
        name: 'Audio Library', 
        description: 'Access personalized meditations, affirmations, and sleep stories',
        icon: Headphones,
        week: 2
      },
      'advanced_analytics': { 
        name: 'Advanced Analytics', 
        description: 'Gain deeper insights into your mental health patterns and progress',
        icon: Brain,
        week: 3
      },
      'detailed_behavioral': { 
        name: 'Behavioral Insights', 
        description: 'Understand your behavioral patterns and how they affect your wellbeing',
        icon: Activity,
        week: 3
      }
    };

    // Return details for the requested feature, or a default if not found
    return featureDetails[featureId] || {
      name: featureId.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
      description: 'This feature will be available soon',
      icon: Lock,
      week: getFeatureWeek(featureId)
    };
  };

  const handleMoodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentMood(parseInt(e.target.value));
  };

  const handleMoodSubmit = () => {
    // In a real app, this would save the mood to the database
    setShowMoodSelector(false);
    // Navigate to journal with pre-filled mood
    navigate('/journal/new', { state: { mood: currentMood } });
  };

  const handleSelectAvatarSession = (sessionType: SessionType) => {
    console.log("Selected avatar session type:", sessionType);
    setShowAvatarSessionModal(false);
    // Update avatar companion state via prop to show it with the selected session type
    setAvatarCompanionState({
      isVisible: true,
      sessionType: sessionType
    });
  };


  const getMoodEmoji = (mood: number) => {
    if (mood <= 2) return '😢';
    if (mood <= 4) return '😔';
    if (mood <= 6) return '😐';
    if (mood <= 8) return '🙂';
    return '😊';
  };

  // Show loading state while auth is being determined
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-stone-50 to-teal-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (!user || !subscription) return null;

  const weekNumber = getWeekNumber();

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-stone-50 to-teal-50 pb-16 md:pb-0">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md shadow-sm border-b border-orange-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-teal-400 to-teal-500 rounded-lg flex items-center justify-center">
                <Home className="w-4 h-4 text-white" />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-teal-600 to-orange-500 bg-clip-text text-transparent">
                Mira
              </span>
            </div>

            {/* Desktop Navigation */}
            <Navigation variant="desktop" />

            {/* Crisis Button - Always Visible */}
            <button
              onClick={() => navigate('/crisis')}
              className="md:hidden p-2 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors"
              aria-label="Crisis Support"
            >
              <Shield className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Welcome Section */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900 mb-1">
            Good {timeOfDay}, {user.firstName}!
          </h1>
          <p className="text-slate-600 text-sm">
            How are you feeling today?
          </p>
        </div>

        {/* Crisis Alert Banner */}
        {(currentCrisisLevel === 'high' || currentCrisisLevel === 'critical') && (
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-red-900">
                  {currentCrisisLevel === 'critical' ? 'Crisis Support Available' : 'Support Recommended'}
                </h3>
                <p className="text-sm text-red-800">
                  {currentCrisisLevel === 'critical' 
                    ? 'I\'m here to support you through this crisis. You\'re not alone.'
                    : 'I\'ve noticed some concerning patterns. Let\'s talk about how you\'re feeling.'
                  }
                </p>
              </div>
              <button
                onClick={() => navigate('/crisis')}
                className="bg-red-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-red-700 transition-colors"
              >
                Get Support
              </button>
            </div>
          </div>
        )}

        {/* Primary Action Card */}
        <div 
          onClick={handleJournalClick}
          className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300 border border-slate-200 mb-6 cursor-pointer"
        >
          <div className="flex items-start justify-between">
            <div>
              <div className="w-14 h-14 bg-gradient-to-br from-teal-400 to-teal-500 rounded-xl flex items-center justify-center mb-3">
                <Edit3 className="w-7 h-7 text-white" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Start Journal Entry</h2>
              <p className="text-slate-600 mb-3 text-sm">
                Express your thoughts and track your mood
              </p>
              
              {stats.streakDays > 0 && (
                <div className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs">
                  <span className="text-orange-600">🔥</span>
                  {stats.streakDays} day streak
                </div>
              )}
            </div>
            
            <div className="hidden md:block">
              <div className="flex items-center gap-1 text-teal-600 font-medium group">
                Start Writing
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2 space-y-4">
            {/* Secondary Actions */}
            <div className="grid grid-cols-2 gap-4">
              {/* AI Companion Card - Week 2+ */}
              <div 
                onClick={() => handleFeatureClick('avatar_companion', '/avatar', true)}
                className={`bg-white rounded-xl p-4 shadow-md hover:shadow-lg transition-all duration-300 border border-slate-200 cursor-pointer ${
                  !isFeatureEnabled('avatar_companion') ? 'opacity-70' : ''
                }`}
              >
                <div className="flex flex-col h-full">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-purple-500 rounded-lg flex items-center justify-center mb-2 relative">
                    <Video className="w-5 h-5 text-white" />
                    {!isFeatureEnabled('avatar_companion') && (
                      <div className="absolute -top-2 -right-2 w-5 h-5 bg-slate-700 rounded-full flex items-center justify-center">
                        <Lock className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1">Tavus Avatar</h3>
                  <p className="text-xs text-slate-600 mb-2 flex-grow">
                    Video chat with your AI therapist
                  </p>
                  {!isFeatureEnabled('avatar_companion') && (
                    <div className="text-xs text-orange-600 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      Week 2
                    </div>
                  )}
                  {isFeatureEnabled('avatar_companion') && !checkUsageLimit('avatar_companion') && (
                    <div className="text-xs text-purple-600 flex items-center gap-1">
                      <Zap className="w-3 h-3" />
                      Pro feature
                    </div>
                  )}
                </div>
              </div>

              {/* Voice Journal Card */}
              <div 
                onClick={() => {
                  console.log("Attempting to navigate to voice journal from Dashboard.");
                  if (checkUsageLimit('voice_journal')) {
                    trackFeatureUsage('voice_journal');
                    navigate('/journal/voice');
                  } else {
                    console.log("Voice journal limit reached. Redirecting to subscription plans.");
                    navigate('/subscription/plans');
                  }
                }}
                className="bg-white rounded-xl p-4 shadow-md hover:shadow-lg transition-all duration-300 border border-slate-200 cursor-pointer"
              >
                <div className="flex flex-col h-full">
                  <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-orange-500 rounded-lg flex items-center justify-center mb-2">
                    <Mic className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1">Voice Journal</h3>
                  <p className="text-xs text-slate-600">
                    Record your thoughts naturally
                  </p>
                  {subscription.tier === 'basic' && (
                    <div className="mt-2 text-xs text-slate-600">
                      {subscription.usage.voiceJournalEntries}/{subscription.plan.limits.voiceJournalEntries} entries used
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Tertiary Actions */}
            <div className="grid grid-cols-2 gap-4">
              {/* Crisis Support Card - Always Available */}
              <div 
                onClick={() => navigate('/crisis')}
                className="bg-white rounded-xl p-4 shadow-md hover:shadow-lg transition-all duration-300 border border-slate-200 cursor-pointer"
              >
                <div className="flex flex-col h-full">
                  <div className="w-10 h-10 bg-gradient-to-br from-red-400 to-red-500 rounded-lg flex items-center justify-center mb-2">
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1">Crisis Support</h3>
                  <p className="text-xs text-slate-600 mb-2">
                    Get immediate help when needed
                  </p>
                  <div className="text-xs text-green-600 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    Free for all users
                  </div>
                </div>
              </div>

              {/* Safety Plan Card */}
              <div 
                onClick={() => navigate('/safety-plan')}
                className="bg-white rounded-xl p-4 shadow-md hover:shadow-lg transition-all duration-300 border border-slate-200 cursor-pointer"
              >
                <div className="flex flex-col h-full">
                  <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-teal-500 rounded-lg flex items-center justify-center mb-2">
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1">Safety Plan</h3>
                  <p className="text-xs text-slate-600 mb-2">
                    {safetyPlan ? 'View or update your safety plan' : 'Create your personal safety plan'}
                  </p>
                  <div className="text-xs text-green-600 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    Free for all users
                  </div>
                </div>
              </div>
            </div>

            {/* Audio Library Card - Week 2+ */}
            <div 
              onClick={() => handleFeatureClick('audio_library', '/audio')}
              className={`bg-white rounded-xl p-4 shadow-md hover:shadow-lg transition-all duration-300 border border-slate-200 cursor-pointer ${
                !isFeatureEnabled('audio_library') ? 'opacity-70' : ''
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-green-500 rounded-lg flex items-center justify-center relative">
                  <Headphones className="w-5 h-5 text-white" />
                  {!isFeatureEnabled('audio_library') && (
                    <div className="absolute -top-2 -right-2 w-5 h-5 bg-slate-700 rounded-full flex items-center justify-center">
                      <Lock className="w-3 h-3 text-white" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900 mb-1">Audio Library</h3>
                  <p className="text-xs text-slate-600">
                    Meditations, affirmations & guided exercises
                  </p>
                </div>
                {!isFeatureEnabled('audio_library') ? (
                  <div className="text-xs text-orange-600 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    Week 2
                  </div>
                ) : (
                  <div className="flex items-center gap-1 text-teal-600 group">
                    <span className="text-sm font-medium">Explore</span>
                    <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Progress Section */}
            <div className="bg-white rounded-xl shadow-md border border-slate-200 p-4">
              <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-teal-600" />
                Your Progress
              </h3>
              
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="text-center">
                  <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-teal-500 rounded-lg flex items-center justify-center mx-auto mb-1">
                    <FileText className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-lg font-bold text-slate-900">{stats.totalEntries}</div>
                  <p className="text-xs text-slate-600">Entries</p>
                </div>
                
                <div className="text-center">
                  <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-orange-500 rounded-lg flex items-center justify-center mx-auto mb-1">
                    <span className="text-lg text-white">{getMoodEmoji(stats.averageMood)}</span>
                  </div>
                  <div className="text-lg font-bold text-slate-900">{stats.averageMood.toFixed(1)}</div>
                  <p className="text-xs text-slate-600">Avg Mood</p>
                </div>
              </div>
              
              {/* Journal Entry Limit for Basic Users */}
              {subscription.tier === 'basic' && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="text-sm font-medium text-slate-900">Text Journal Entries</h4>
                    <span className="text-xs text-slate-600">
                      {subscription.usage.journalEntries}/{subscription.plan.limits.journalEntries} this month
                    </span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2 mb-3">
                    <div 
                      className="bg-gradient-to-r from-teal-500 to-teal-600 h-2 rounded-full" 
                      style={{ 
                        width: `${Math.min(100, (subscription.usage.journalEntries / (subscription.plan.limits.journalEntries as number)) * 100)}%` 
                      }}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="text-sm font-medium text-slate-900">Voice Journal Entries</h4>
                    <span className="text-xs text-slate-600">
                      {subscription.usage.voiceJournalEntries}/{subscription.plan.limits.voiceJournalEntries} this month
                    </span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2 mb-1">
                    <div 
                      className="bg-gradient-to-r from-orange-500 to-orange-600 h-2 rounded-full" 
                      style={{ 
                        width: `${Math.min(100, (subscription.usage.voiceJournalEntries / (subscription.plan.limits.voiceJournalEntries as number)) * 100)}%` 
                      }}
                    />
                  </div>
                </div>
              )}
              
              {/* Goal Progress */}
              {user.profile?.mentalHealthGoals && user.profile.mentalHealthGoals.length > 0 && (
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="text-sm font-medium text-slate-900">Current Goal</h4>
                    <span className="text-xs text-slate-600">45% Complete</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2 mb-1">
                    <div className="bg-gradient-to-r from-teal-500 to-teal-600 h-2 rounded-full" style={{ width: '45%' }}></div>
                  </div>
                  <p className="text-xs text-slate-600 truncate">{user.profile.mentalHealthGoals[0]}</p>
                </div>
              )}
            </div>
            
            {/* Subscription Status */}
            <div className="bg-white rounded-xl shadow-md border border-slate-200 p-4">
              <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-teal-600" />
                Subscription Status
              </h3>
              
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  subscription.tier === 'basic' ? 'bg-slate-100' :
                  subscription.tier === 'plus' ? 'bg-teal-100' :
                  subscription.tier === 'pro' ? 'bg-purple-100' :
                  'bg-orange-100'
                }`}>
                  {subscription.tier === 'basic' ? (
                    <Heart className={`w-5 h-5 ${
                      subscription.tier === 'basic' ? 'text-slate-600' :
                      subscription.tier === 'plus' ? 'text-teal-600' :
                      subscription.tier === 'pro' ? 'text-purple-600' :
                      'text-orange-600'
                    }`} />
                  ) : (
                    <Zap className={`w-5 h-5 ${
                      subscription.tier === 'basic' ? 'text-slate-600' :
                      subscription.tier === 'plus' ? 'text-teal-600' :
                      subscription.tier === 'pro' ? 'text-purple-600' :
                      'text-orange-600'
                    }`} />
                  )}
                </div>
                <div className="flex-1">
                  <div className="font-medium text-slate-900">{subscription.plan.name}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {subscription.status === 'trial' ? 'Trial ends' : 'Renews'}: {format(subscription.currentPeriodEnd, 'MMM d, yyyy')}
                  </div>
                </div>
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                  subscription.status === 'active' ? 'bg-green-100 text-green-700' :
                  subscription.status === 'trial' ? 'bg-blue-100 text-blue-700' :
                  subscription.status === 'paused' ? 'bg-orange-100 text-orange-700' :
                  subscription.status === 'canceled' ? 'bg-red-100 text-red-700' :
                  'bg-slate-100 text-slate-700'
                }`}>
                  {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
                </div>
              </div>
              
              {/* Usage limits for Basic users */}
              {subscription.tier === 'basic' && (
                <div className="space-y-2 mb-3">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="text-xs font-medium text-slate-700">Journal Entries</h4>
                      <span className="text-xs text-slate-600">
                        {subscription.usage.journalEntries}/{subscription.plan.limits.journalEntries}
                      </span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-1.5">
                      <div 
                        className="bg-gradient-to-r from-teal-500 to-teal-600 h-1.5 rounded-full" 
                        style={{ 
                          width: `${Math.min(100, (subscription.usage.journalEntries / (subscription.plan.limits.journalEntries as number)) * 100)}%` 
                        }}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="text-xs font-medium text-slate-700">Voice Journal</h4>
                      <span className="text-xs text-slate-600">
                        {subscription.usage.voiceJournalEntries}/{subscription.plan.limits.voiceJournalEntries}
                      </span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-1.5">
                      <div 
                        className="bg-gradient-to-r from-orange-500 to-orange-600 h-1.5 rounded-full" 
                        style={{ 
                          width: `${Math.min(100, (subscription.usage.voiceJournalEntries / (subscription.plan.limits.voiceJournalEntries as number)) * 100)}%` 
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
              
              {/* Subscription action button */}
              {subscription.tier === 'basic' ? (
                <button
                  onClick={() => navigate('/subscription/plans')}
                  className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-teal-500 to-teal-600 text-white py-2 px-3 rounded-lg hover:from-teal-600 hover:to-teal-700 transition-colors"
                >
                  <Zap className="w-4 h-4" />
                  Upgrade to Plus
                </button>
              ) : (
                <button
                  onClick={() => navigate('/subscription/manage')}
                  className="w-full flex items-center justify-center gap-2 border border-slate-300 text-slate-700 py-2 px-3 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <CreditCard className="w-4 h-4" />
                  Manage Subscription
                </button>
              )}
              
              {/* Price display for paid plans */}
              {subscription.tier !== 'basic' && (
                <div className="mt-2 text-center text-xs text-slate-500 flex items-center justify-center gap-1">
                  <DollarSign className="w-3 h-3" />
                  {subscription.plan.price}/{subscription.plan.billingPeriod === 'yearly' ? 'year' : 'month'}
                </div>
              )}
            </div>

            {/* Safety Plan Status */}
            <div className="bg-white rounded-xl shadow-md border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-900">Safety Plan</h3>
                <div className={`px-2 py-0.5 rounded-full text-xs ${safetyPlan ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                  {safetyPlan ? 'Created' : 'Not Created'}
                </div>
              </div>
              
              <p className="text-sm text-slate-600 mb-3">
                {safetyPlan 
                  ? 'Your safety plan is ready to help during difficult times.' 
                  : 'Create a personalized plan to help during difficult times.'}
              </p>
              
              <button
                onClick={() => navigate('/safety-plan')}
                className="w-full flex items-center justify-center gap-2 bg-teal-500 text-white py-2 px-3 rounded-lg hover:bg-teal-600 transition-colors"
              >
                <Shield className="w-4 h-4" />
                {safetyPlan ? 'View Safety Plan' : 'Create Safety Plan'}
              </button>
            </div>

            {/* Crisis Support Card - Highlighted */}
            <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-xl p-4 text-white shadow-md">
              <div className="flex items-center gap-2 mb-3">
                <Shield className="w-5 h-5" />
                <h3 className="font-semibold">Crisis Support</h3>
              </div>
              <p className="text-sm text-white/90 mb-3">
                Free crisis resources and safety planning tools are available to all users, regardless of subscription level.
              </p>
              <button
                onClick={() => navigate('/crisis')}
                className="w-full bg-white text-red-600 py-2 px-3 rounded-lg font-medium hover:bg-red-50 transition-colors text-sm"
              >
                Access Support
              </button>
            </div>

            {/* Upcoming Features Card */}
            <UpcomingFeaturesCard />
          </div>
        </div>
      </main>
      
      {/* Feature Locked Modal */}
      {featureLockedModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                {React.createElement(getFeatureDetails(featureLockedModal).icon, { className: "w-8 h-8 text-white" })}
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Feature Coming Soon</h3>
              <p className="text-slate-600 mb-1">
                <span className="font-semibold">{getFeatureDetails(featureLockedModal).name}</span> will be available in week {getFeatureDetails(featureLockedModal).week}.
              </p>
              <p className="text-slate-500 text-sm">
                {getFeatureDetails(featureLockedModal).description}
              </p>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-2">
                <Calendar className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-blue-800 font-medium">Keep using Mira to unlock this feature!</p>
                  <p className="text-xs text-blue-700 mt-1">
                    Features are gradually unlocked as you continue your mental health journey with Mira.
                  </p>
                </div>
              </div>
            </div>
            
            <button
              onClick={() => setFeatureLockedModal(null)}
              className="w-full py-3 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-xl font-semibold hover:from-teal-600 hover:to-teal-700 transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      )}
      
      {/* Avatar Session Modal */}
      {showAvatarSessionModal && (
        <AvatarSessionModal 
          onSelectSession={handleSelectAvatarSession}
          onClose={() => setShowAvatarSessionModal(false)}
        />
      )}

      {/* Mobile Navigation */}
      <Navigation />

      {/* Mood Selector Modal */}
      {showMoodSelector && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="text-5xl mb-4">{getMoodEmoji(currentMood)}</div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">How are you feeling?</h3>
              <p className="text-slate-600 text-sm">
                Tracking your mood helps us provide better support
              </p>
            </div>
            
            <div className="mb-6">
              <div className="flex justify-between mb-2">
                <span className="text-sm text-slate-500">Not great</span>
                <span className="text-sm text-slate-500">Great</span>
              </div>
              <input
                type="range"
                min="1"
                max="10"
                value={currentMood}
                onChange={handleMoodChange}
                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between mt-2">
                <span className="text-xs text-slate-600">1</span>
                <span className="text-xs text-slate-600">10</span>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowMoodSelector(false)}
                className="flex-1 px-4 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleMoodSubmit}
                className="flex-1 bg-gradient-to-r from-teal-500 to-teal-600 text-white px-4 py-3 rounded-lg hover:from-teal-600 hover:to-teal-700 transition-colors"
              >
                Start Journaling
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Home = (props: any) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

const BookOpen = (props: any) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
  </svg>
);

export default Dashboard;